import { useCallback, useRef, useState } from 'react';

/**
 * Tracks when a <video> has metadata so callers can show/play it.
 * Does not seek-to-end for WebM Infinity duration — that forced a near-full
 * download and made 20–30s intros feel stuck on buffering. New WebM uploads
 * get duration fixed at record time via fix-webm-duration.
 */
export function useVideoDuration(playWhenReady = true) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const playWhenReadyRef = useRef(playWhenReady);
  playWhenReadyRef.current = playWhenReady;

  const onLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    setReady(true);
    if (playWhenReadyRef.current) {
      video.play().catch(() => {});
    }
  }, []);

  const reset = useCallback(() => setReady(false), []);

  return { videoRef, ready, onLoadedMetadata, reset };
}
